import type { PrometheusQuery } from '../api/types';
import type { StatPanelDef } from './serviceOverview';

function rateInterval(preset: string): string {
  switch (preset) {
    case '1h': return '1m';
    case '6h': return '5m';
    case '24h': return '15m';
    case '7d': return '1h';
    default: return '5m';
  }
}

export const INFRA_STAT_PANELS: StatPanelDef[] = [
  {
    title: 'Pod Restarts',
    unit: 'short',
    refId: 'podRestarts',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 3 },
      { color: 'red', value: 10 },
    ],
  },
  {
    title: 'Ready Pods',
    unit: 'short',
    refId: 'readyPods',
    thresholds: [
      { color: 'red', value: 0 },
      { color: 'yellow', value: 1 },
      { color: 'green', value: 2 },
    ],
  },
  {
    title: 'Running Containers',
    unit: 'short',
    refId: 'runningContainers',
    thresholds: [
      { color: 'red', value: 0 },
      { color: 'yellow', value: 1 },
      { color: 'green', value: 2 },
    ],
  },
  {
    title: 'Waiting Containers',
    unit: 'short',
    refId: 'waitingContainers',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 1 },
      { color: 'red', value: 3 },
    ],
  },
  {
    title: 'Node CPU %',
    unit: 'percentunit',
    refId: 'nodeCpu',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.7 },
      { color: 'red', value: 0.9 },
    ],
  },
  {
    title: 'Node Memory %',
    unit: 'percentunit',
    refId: 'nodeMemory',
    thresholds: [
      { color: 'green', value: 0 },
      { color: 'yellow', value: 0.7 },
      { color: 'red', value: 0.9 },
    ],
  },
];

export interface InfraTimePanelDef {
  title: string;
  unit: string;
  refId: string;
  thresholdLine?: { value: number; color: string };
}

export const INFRA_TIME_PANELS: Record<string, InfraTimePanelDef> = {
  restartHistory: {
    title: 'Pod Restart History',
    unit: 'short',
    refId: 'restartHistory',
  },
  cpuOverTime: {
    title: 'Node CPU Utilisation',
    unit: 'percentunit',
    refId: 'cpuOverTime',
    thresholdLine: { value: 0.9, color: '#FF462D' },
  },
  memoryOverTime: {
    title: 'Node Memory Utilisation',
    unit: 'percentunit',
    refId: 'memoryOverTime',
    thresholdLine: { value: 0.9, color: '#FF462D' },
  },
};

/**
 * Build the PromQL queries for infrastructure stat cards.
 */
export function buildInfraStatQueries(
  namespace: string,
  serviceName: string,
  preset: string,
): PrometheusQuery[] {
  const ri = rateInterval(preset);

  return [
    {
      expr: `sum(kube_pod_container_status_restarts_total{namespace="${namespace}", pod=~"${serviceName}.*"})`,
      refId: 'podRestarts',
      instant: true,
    },
    {
      expr: `sum(kube_pod_status_ready{namespace="${namespace}", pod=~"${serviceName}.*", condition="true"})`,
      refId: 'readyPods',
      instant: true,
    },
    {
      expr: `sum(kube_pod_container_status_running{namespace="${namespace}", pod=~"${serviceName}.*"})`,
      refId: 'runningContainers',
      instant: true,
    },
    {
      expr: `sum(kube_pod_container_status_waiting{namespace="${namespace}", pod=~"${serviceName}.*"})`,
      refId: 'waitingContainers',
      instant: true,
    },
    {
      expr: `avg(1 - rate(node_cpu_seconds_total{mode="idle"}[${ri}]))`,
      refId: 'nodeCpu',
      instant: true,
    },
    {
      expr: `avg(1 - node_memory_MemAvailable_bytes / node_memory_MemTotal_bytes)`,
      refId: 'nodeMemory',
      instant: true,
    },
  ];
}

/**
 * Build the PromQL queries for infrastructure time series charts.
 */
export function buildInfraTimeSeriesQueries(
  namespace: string,
  serviceName: string,
  preset: string,
): PrometheusQuery[] {
  const ri = rateInterval(preset);

  return [
    {
      expr: `sum(increase(kube_pod_container_status_restarts_total{namespace="${namespace}", pod=~"${serviceName}.*"}[${ri}]))`,
      refId: 'restartHistory',
      legendFormat: 'Restarts',
    },
    {
      expr: `avg(1 - rate(node_cpu_seconds_total{mode="idle"}[${ri}]))`,
      refId: 'cpuOverTime',
      legendFormat: 'CPU %',
    },
    {
      expr: `avg(1 - node_memory_MemAvailable_bytes / node_memory_MemTotal_bytes)`,
      refId: 'memoryOverTime',
      legendFormat: 'Memory %',
    },
  ];
}
